import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Bath,
  BedDouble,
  Check,
  MapPin,
  Phone,
} from "lucide-react";

import properties from "../data/properties";

const PropertyDetails = () => {
  const { slug } = useParams();

  const property = properties.find(
    (item) => item.slug === slug
  );

  if (!property) {
    return (
      <main className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-xl">
          <p className="text-xs uppercase tracking-[0.25em] text-blue-600 font-semibold">
            Property not found
          </p>

          <h1 className="mt-4 text-3xl md:text-5xl font-semibold text-gray-900">
            This property is no longer available here.
          </h1>

          <p className="mt-5 text-gray-600 leading-7">
            The property link may have changed or the listing may
            have been removed.
          </p>

          <Link
            to="/properties"
            className="mt-8 inline-flex items-center gap-3 bg-gray-900 text-white px-7 py-4 text-sm font-semibold hover:bg-blue-600 transition"
          >
            <ArrowLeft size={17} />
            Back to Properties
          </Link>
        </div>
      </main>
    );
  }

  const otherProperties = properties
    .filter((item) => item.id !== property.id)
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>
          {property.title} | Solonet Properties
        </title>

        <meta
          name="description"
          content={property.excerpt}
        />

        <meta
          property="og:title"
          content={`${property.title} | Solonet Properties`}
        />

        <meta
          property="og:description"
          content={property.excerpt}
        />

        <meta
          property="og:image"
          content={property.coverImage}
        />

        <meta
          property="og:url"
          content={`https://solonetproperties.com/properties/${property.slug}`}
        />

        <meta
          property="og:type"
          content="article"
        />
      </Helmet>

      <main className="bg-white min-h-screen">
        {/* HERO */}
        <section className="relative min-h-[78vh] md:min-h-[88vh] flex items-end overflow-hidden bg-gray-900">
          <img
            src={property.coverImage}
            alt={property.title}
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />

          <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-14 md:pb-20 pt-40">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="bg-white text-gray-900 px-4 py-2 text-[10px] md:text-[11px] uppercase tracking-[0.2em] font-semibold">
                  {property.status}
                </span>

                <span className="border border-white/30 bg-black/20 backdrop-blur-sm text-white px-4 py-2 text-[10px] md:text-[11px] uppercase tracking-[0.2em] font-semibold">
                  {property.category}
                </span>
              </div>

              <h1 className="mt-7 text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.04] text-white">
                {property.title}
              </h1>

              <div className="mt-6 flex items-center gap-2 text-gray-200">
                <MapPin
                  size={19}
                  strokeWidth={1.7}
                />

                <span className="text-base md:text-lg">
                  {property.location}
                </span>
              </div>

              <div className="mt-8">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-gray-300 font-semibold">
                  {property.priceLabel}
                </p>

                <p className="mt-2 text-2xl md:text-4xl text-white font-semibold">
                  {property.price}
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* INTRO / SUMMARY */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-14 lg:gap-24">
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs uppercase tracking-[0.25em] text-blue-600 font-semibold">
                About This Property
              </p>

              <h2 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight text-gray-900 leading-tight">
                Designed for modern
                <span className="block text-gray-400">
                  living.
                </span>
              </h2>

              <p className="mt-7 text-base md:text-lg leading-8 text-gray-600">
                {property.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.7,
                delay: 0.1,
              }}
              className="border-t lg:border-t-0 lg:border-l border-gray-200 pt-8 lg:pt-0 lg:pl-12"
            >
              <p className="text-xs uppercase tracking-[0.25em] text-gray-400 font-semibold">
                Property Details
              </p>

              <div className="mt-8 space-y-7">
                {property.bedrooms && (
                  <div className="flex items-center justify-between gap-5 border-b border-gray-100 pb-6">
                    <div className="flex items-center gap-3 text-gray-500">
                      <BedDouble
                        size={20}
                        strokeWidth={1.6}
                      />
                      <span>Bedrooms</span>
                    </div>

                    <span className="text-xl font-semibold text-gray-900">
                      {property.bedrooms}
                    </span>
                  </div>
                )}

                {property.bathrooms && (
                  <div className="flex items-center justify-between gap-5 border-b border-gray-100 pb-6">
                    <div className="flex items-center gap-3 text-gray-500">
                      <Bath
                        size={20}
                        strokeWidth={1.6}
                      />
                      <span>Bathrooms</span>
                    </div>

                    <span className="text-xl font-semibold text-gray-900">
                      {property.bathrooms}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-5 border-b border-gray-100 pb-6">
                  <div className="flex items-center gap-3 text-gray-500">
                    <MapPin
                      size={20}
                      strokeWidth={1.6}
                    />
                    <span>Location</span>
                  </div>

                  <span className="text-right font-semibold text-gray-900">
                    {property.location}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-5">
                  <span className="text-gray-500">
                    Status
                  </span>

                  <span className="font-semibold text-gray-900">
                    {property.status}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* VIDEO */}
        {property.video && (
          <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-600 font-semibold">
                Property Tour
              </p>

              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-gray-900">
                Take a closer look.
              </h2>
            </div>

            <video
              src={property.video}
              controls
              playsInline
              poster={property.coverImage}
              className="w-full max-h-[720px] object-cover bg-black"
            >
              Your browser does not support this video.
            </video>
          </section>
        )}

        {/* FEATURES */}
        {property.features &&
          property.features.length > 0 && (
            <section className="bg-[#f7f7f5]">
              <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-12 lg:gap-20">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-blue-600 font-semibold">
                      Features
                    </p>

                    <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-gray-900">
                      What this property
                      <span className="block text-gray-400">
                        has to offer.
                      </span>
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-x-10">
                    {property.features.map(
                      (feature, index) => (
                        <motion.div
                          key={index}
                          initial={{
                            opacity: 0,
                            y: 15,
                          }}
                          whileInView={{
                            opacity: 1,
                            y: 0,
                          }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.45,
                            delay: index * 0.03,
                          }}
                          className="flex items-start gap-3 py-5 border-b border-gray-200"
                        >
                          <div className="mt-0.5 w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0">
                            <Check
                              size={13}
                              strokeWidth={2}
                              className="text-blue-600"
                            />
                          </div>

                          <p className="text-gray-700 leading-6">
                            {feature}
                          </p>
                        </motion.div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

        {/* GALLERY */}
        {property.images &&
          property.images.length > 0 && (
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
              <div className="mb-10 md:mb-14">
                <p className="text-xs uppercase tracking-[0.25em] text-blue-600 font-semibold">
                  Gallery
                </p>

                <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-gray-900">
                  Explore the property.
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                {property.images.map(
                  (image, index) => (
                    <motion.div
                      key={image}
                      initial={{
                        opacity: 0,
                        y: 20,
                      }}
                      whileInView={{
                        opacity: 1,
                        y: 0,
                      }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.55,
                        delay: Math.min(
                          index * 0.04,
                          0.3
                        ),
                      }}
                      className={`overflow-hidden bg-gray-100 ${
                        index === 0
                          ? "md:col-span-2"
                          : ""
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${property.title} - ${
                          index + 1
                        }`}
                        loading={
                          index > 2
                            ? "lazy"
                            : "eager"
                        }
                        className={`w-full object-cover transition-transform duration-700 hover:scale-[1.02] ${
                          index === 0
                            ? "h-[360px] md:h-[650px]"
                            : "h-[300px] md:h-[450px]"
                        }`}
                      />
                    </motion.div>
                  )
                )}
              </div>
            </section>
          )}

        {/* ENQUIRY */}
        <section className="bg-[#0d1117] text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid lg:grid-cols-[1fr_auto] lg:items-end gap-10">
              <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.28em] text-blue-400 font-semibold">
                  Interested?
                </p>

                <h2 className="mt-5 text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight">
                  Make this property
                  <span className="block text-gray-500">
                    your next move.
                  </span>
                </h2>

                <p className="mt-6 max-w-xl text-gray-400 leading-7">
                  Contact Solonet Properties for further
                  information, availability, pricing or to
                  arrange a property viewing.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3">
                <a
                  href="tel:+233534065455"
                  className="inline-flex items-center justify-center gap-3 bg-white text-gray-900 px-7 py-4 text-sm font-semibold hover:bg-gray-100 transition"
                >
                  <Phone size={17} />
                  Call Solonet
                </a>

                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 border border-white/25 text-white px-7 py-4 text-sm font-semibold hover:bg-white hover:text-gray-900 transition"
                >
                  Send Enquiry
                  <ArrowUpRight size={17} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* OTHER PROPERTIES */}
        {otherProperties.length > 0 && (
          <section className="bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-7 mb-10">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-blue-600 font-semibold">
                    More Properties
                  </p>

                  <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-gray-900">
                    You may also like
                  </h2>
                </div>

                <Link
                  to="/properties"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-blue-600 transition"
                >
                  View all properties
                  <ArrowUpRight size={16} />
                </Link>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {otherProperties.map(
                  (item) => (
                    <Link
                      key={item.id}
                      to={`/properties/${item.slug}`}
                      className="group"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                        <img
                          src={item.coverImage}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>

                      <div className="pt-5">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-blue-600 font-semibold">
                          {item.category}
                        </p>

                        <h3 className="mt-2 text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-sm text-gray-500">
                          {item.location}
                        </p>

                        <p className="mt-4 font-semibold text-gray-900">
                          {item.price}
                        </p>
                      </div>
                    </Link>
                  )
                )}
              </div>
            </div>
          </section>
        )}

        {/* BACK */}
        <section className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
            <Link
              to="/properties"
              className="inline-flex items-center gap-3 text-sm font-semibold text-gray-700 hover:text-blue-600 transition"
            >
              <ArrowLeft size={17} />
              Back to all properties
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default PropertyDetails;